import { useState, useEffect } from "react";
import { LinkFieldSearch } from "./LinkFieldSearch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import type { FilterDescriptor } from "@/lib/erp";

type TranslationModule = Record<string, string>;

interface AssignedToFilterProps {
  value: FilterDescriptor[];
  onChange: (filters: FilterDescriptor[]) => void;
  translations?: TranslationModule;
}

export function AssignedToFilter({
  value,
  onChange,
  translations,
}: AssignedToFilterProps) {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [logicMode, setLogicMode] = useState<"any" | "all">("all");
  const [currentInput, setCurrentInput] = useState<string | null>(null);

  // Extract selected users from filters on mount/change
  useEffect(() => {
    const users = value
      .filter((f) => f.field === "_assigned_to" && f._metadata?.section === "assigned_to")
      .map((f) => {
        // Value is now a plain email string (for "=") or array (for "in")
        if (f.operator === "in" && Array.isArray(f.value)) {
          return f.value;
        }
        return String(f.value);
      })
      .flat() // Flatten in case we have arrays
      .filter((u): u is string => u !== null && u !== "");

    setSelectedUsers(users);

    // Determine logic mode from first filter's metadata
    const firstFilter = value.find(
      (f) => f.field === "_assigned_to" && f._metadata?.section === "assigned_to"
    );
    if (firstFilter?._metadata?.logicMode) {
      setLogicMode(firstFilter._metadata.logicMode);
    }
  }, [value]);

  const handleUserSelect = (userEmail: string | null) => {
    if (!userEmail) return;

    // Add user if not already selected
    if (!selectedUsers.includes(userEmail)) {
      const newUsers = [...selectedUsers, userEmail];
      setSelectedUsers(newUsers);
      updateFilters(newUsers, logicMode);
    }

    setCurrentInput(null);
  };

  const handleRemoveUser = (userEmail: string) => {
    const newUsers = selectedUsers.filter((u) => u !== userEmail);
    setSelectedUsers(newUsers);
    updateFilters(newUsers, logicMode);
  };

  const handleLogicModeChange = (mode: "any" | "all") => {
    setLogicMode(mode);
    updateFilters(selectedUsers, mode);
  };

  const updateFilters = (users: string[], mode: "any" | "all") => {
    // Remove existing assigned_to filters
    const otherFilters = value.filter(
      (f) => !(f.field === "_assigned_to" && f._metadata?.section === "assigned_to")
    );

    // Create new filters for each user
    // Use _assigned_to field - backend will extract from filters and send as assigned_to in POST body
    let newFilters: FilterDescriptor[];
    
    if (mode === "any" && users.length > 1) {
      // ANY mode with multiple users: send as single filter with "in" operator
      newFilters = [{
        field: "_assigned_to",
        operator: "in",
        value: users, // Array of emails
        _metadata: {
          section: "assigned_to",
          logicMode: "any",
        },
      }];
    } else {
      // ALL mode or single user: send individual filters with "=" operator
      // Backend will accumulate these into a list for ALL mode
      newFilters = users.map((userEmail) => ({
        field: "_assigned_to",
        operator: "equals",
        value: userEmail, // Plain email string, no escaping
      _metadata: {
        section: "assigned_to",
        logicMode: mode,
      },
    }));
    }

    onChange([...otherFilters, ...newFilters]);
  };

  const t = translations || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        {selectedUsers.length > 1 && (
          <div className="flex items-center gap-2">
            <Button
              variant={logicMode === "any" ? "default" : "outline"}
              size="sm"
              onClick={() => handleLogicModeChange("any")}
              className="h-8 text-xs"
            >
              {t.assignedToAny || "ANY"}
            </Button>
            <Button
              variant={logicMode === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => handleLogicModeChange("all")}
              className="h-8 text-xs"
            >
              {t.assignedToAll || "ALL"}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <LinkFieldSearch
          doctype="User"
          value={currentInput}
          onChange={handleUserSelect}
          placeholder={t.selectUser || "Select user..."}
          fetchInitialItems={true}
          initialSearch="" // Changed from "%" to "" to avoid Frappe bug in some versions
          className="w-full"
        />

        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedUsers.map((userEmail) => (
              <Badge
                key={userEmail}
                variant="secondary"
                className="flex items-center gap-1 px-2 py-1"
              >
                <span className="text-xs">{userEmail}</span>
                <button
                  onClick={() => handleRemoveUser(userEmail)}
                  className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                  aria-label={t.removeUser || "Remove user"}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

